import React, { useEffect, useRef, useMemo } from "react";
import "./style.scss";
import Slider from "react-slick";
import { useSelector } from "react-redux";
import { getAppConfig } from "../../../redux/slices/appconfigSlice";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useParams, useLocation } from "react-router";
import ListViewHotelItem from "./ListViewHotelItem";
import { getSearchFilters } from "../../../redux/slices/searchFilterSlice";

function MapCardSlider({ isDesktop, isMobile, handleOnView, isChatOffer }) {
    const { hotelId, type } = useParams(); // Get the numeric ID from the URL
    const location = useLocation();
    const slider = useRef();
    const listRef = useRef(null);
    const programmaticSlideTargetRef = useRef(null);
    const userSlideInteractionRef = useRef(false);
    const navigate = useNavigate();
    const appConfig = useSelector(getAppConfig);
    const searchfilters = useSelector(getSearchFilters);

    // The detail page ("Show on Map") passes the currently selected flight
    // (state.booking.flight) plus its hotelId through router state, because the
    // booking slice is reset when HotelDetails unmounts. Stabilize it in a ref so
    // later in-map navigations (which don't carry it) don't drop it.
    const sharedFlightFromState = location.state?.flight;
    const sharedFlightHotelIdFromState = location.state?.flightHotelId;
    const sharedFlightRef = useRef(null);
    if (sharedFlightFromState) {
        sharedFlightRef.current = {
            flight: sharedFlightFromState,
            hotelId: sharedFlightHotelIdFromState,
        };
    }
    // Only use the passed flight for the hotel it actually belongs to.
    const sharedFlight =
        sharedFlightRef.current &&
        String(sharedFlightRef.current.hotelId) === String(hotelId)
            ? sharedFlightRef.current.flight
            : null;

    const offers = useMemo(() => {
        if (type === 'fav') {
            return [searchfilters?.selectedFavoriteHotel];
        }

        const branchMatch = appConfig?.branchData && String(appConfig.branchData.id) === String(hotelId);
        if (branchMatch) {
            return [appConfig.branchData];
        }

        if (!isChatOffer) {
            return appConfig.offers;
        }

        const chatOffers = appConfig.chatGPTOffers || [];
        // Find the target hotel by hotelId
        const targetHotel = chatOffers.find(f => String(f.id) === String(hotelId));
        if (!targetHotel || !targetHotel.guidForBooking) {
            return [];
        }

        // Filter all offers with the same guidForBooking
        return chatOffers.filter(f => f.guidForBooking === targetHotel.guidForBooking);
    }, [isChatOffer, appConfig.branchData, appConfig.chatGPTOffers, appConfig.offers, hotelId, type]);

    // Inject the shared flight into the selected hotel's offer so the card shows the
    // identical flight/price the user saw on the detail page: replace the flightOffer
    // path (drives the date chips) and refresh perPersonPrice from that flight (the
    // number rendered on the card). No-op for normal/non-shared flows.
    const offersWithSharedFlight = useMemo(() => {
        if (!sharedFlight || !offers?.length) return offers;
        return offers.map((offer) => {
            if (String(offer?.id) !== String(hotelId)) return offer;
            const perPerson = sharedFlight?.price?.averageCostPerPerson;
            return {
                ...offer,
                perPersonPrice: perPerson
                    ? { totalPrice: perPerson.value, currency: perPerson.currency }
                    : offer?.perPersonPrice,
                objHotel: {
                    ...offer?.objHotel,
                    newPackagedOffer: {
                        ...offer?.objHotel?.newPackagedOffer,
                        flightOffer: sharedFlight,
                    },
                },
            };
        });
    }, [offers, sharedFlight, hotelId]);

    const packages = useMemo(() => {
        const foundOffer = offersWithSharedFlight?.find((f) => String(f?.id) === String(hotelId));

        if (foundOffer && isDesktop) {
            return [foundOffer, ...offersWithSharedFlight.filter((f) => String(f?.id) !== String(hotelId))];
        }

        return offersWithSharedFlight || [];
    }, [hotelId, isDesktop, offersWithSharedFlight]);

    // Verification log: per-person price this card is rendering for the selected hotel.
    const selectedForLog = offersWithSharedFlight?.find((f) => String(f?.id) === String(hotelId));
    console.log(
        'MAP pricePerPerson',
        selectedForLog?.perPersonPrice?.totalPrice,
        { hotelId, usedSharedFlight: !!sharedFlight }
    );
    
    useEffect(() => {
        if (isDesktop) {
            if (listRef.current) {
                listRef.current.scrollTop = 0;
            }
            return;
        }

        const timer = setTimeout(() => {
            const index = packages?.findIndex((f) => String(f?.id) === String(hotelId));
            if (index >= 0) {
                programmaticSlideTargetRef.current = index;
                slider?.current?.slickGoTo(index, true);
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [hotelId, isDesktop, packages]);

    const settings = {
        className: "center",
        dots: false,
        centerMode: true,
        // infinite: 3,
        centerPadding: "150px",
        slidesToShow: 9,
        slidesToScroll: 1,
        swipeToSlide: true,
        speed: 200,
        initialSlide: 3,
        arrows: false,
        responsive: [
            {
                breakpoint: 3400,
                settings: {
                    slidesToShow: 8,
                    centerPadding: "50px",
                },
            },
            {
                breakpoint: 2900,
                settings: {
                    slidesToShow: 7,
                    centerPadding: "50px",
                },
            },
            {
                breakpoint: 2550,
                settings: {
                    slidesToShow: 6,
                    centerPadding: "50px",
                },
            },
            {
                breakpoint: 2130,
                settings: {
                    slidesToShow: 5,
                    centerPadding: "150px",
                },
            },
            {
                breakpoint: 1800,
                settings: {
                    slidesToShow: 4,
                    centerPadding: "150px",
                },
            },
            {
                breakpoint: 1530,
                settings: {
                    slidesToShow: 4,
                    centerPadding: "50px",
                },
            },
            {
                breakpoint: 1400,
                settings: {
                    slidesToShow: 3,
                    centerPadding: "100px",
                },
            },
            {
                breakpoint: 1199,
                settings: {
                    slidesToShow: 2,
                    centerPadding: "100px",
                },
            },
            {
                breakpoint: 991,
                settings: {
                    slidesToShow: 1,
                    centerPadding: "200px",
                },
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 1,
                    centerPadding: "100px",
                },
            },
            {
                breakpoint: 576,
                settings: {
                    slidesToShow: 1,
                    centerPadding: "20px",
                },
            },
        ],
    };

    if (!isDesktop && !isMobile) {
        return null;
    }

    return isDesktop ? (
        <div ref={listRef} style={{ maxHeight: "50hv", overflowY: "auto" }}>
            {packages?.length > 1 ? packages?.map((offer, index) => (
                <ListViewHotelItem offer={offer} key={offer?.id || index} isDesktop={isDesktop} isMobile={isMobile} hotelId={hotelId} handleOnView={handleOnView} isChatOffer={isChatOffer} />
            )) : (
                <ListViewHotelItem offer={packages[0]} isDesktop={isDesktop} isMobile={isMobile} hotelId={hotelId} handleOnView={handleOnView} isChatOffer={isChatOffer} />
            )}
        </div>
    ) : (
        <div
            className="mapcards-slider"
            onMouseDown={() => {
                userSlideInteractionRef.current = true;
            }}
            onTouchStart={() => {
                userSlideInteractionRef.current = true;
            }}
        >
            {packages?.length > 1 ? <Slider
                ref={slider}
                {...settings}
                beforeChange={(oldSlide, currentSlide) => {
                    const nextId = packages[currentSlide]?.id;

                    if (programmaticSlideTargetRef.current === currentSlide) {
                        programmaticSlideTargetRef.current = null;
                        return;
                    }

                    if (nextId && userSlideInteractionRef.current && String(nextId) !== String(hotelId)) {
                        userSlideInteractionRef.current = false;
                        navigate(`/hotel/${nextId}/map/${isChatOffer ? 'chatOffer' : 'normal'}`, {
                            replace: true,
                            state: { isChatOffer }
                        });
                    }
                }}
            >
                {packages?.map((offer, index) => (
                    <ListViewHotelItem offer={offer} key={offer?.id || index} isDesktop={isDesktop} isMobile={isMobile} hotelId={hotelId} handleOnView={handleOnView} isChatOffer={isChatOffer} />
                ))}
            </Slider> : (
                <div className="m-3">
                    <ListViewHotelItem offer={packages[0]} isDesktop={isDesktop} isMobile={isMobile} hotelId={hotelId} handleOnView={handleOnView} isChatOffer={isChatOffer} />
                </div>
            )}
        </div>
    );
}

MapCardSlider.propTypes = {
    isDesktop: PropTypes.bool,
    isMobile: PropTypes.bool,
    handleOnView: PropTypes.func,
    isChatOffer: PropTypes.bool
};

export default MapCardSlider;
